// features/auth/Register.js - Registration component
import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useTranslation} from 'react-i18next';
import {FaUserPlus} from 'react-icons/fa';
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from '../../components/ui/card';
import {Button} from '../../components/ui/button';
import {useAuth} from '../../context/AuthContext';
import LoadingOverlay from '../../components/LoadingOverlay';
import {GoogleLogin} from "@react-oauth/google";

const Register = () => {
	const {t} = useTranslation();
	const {register, socialLogin, loading, error} = useAuth();
	const navigate = useNavigate();
	
	const [formData, setFormData] = useState({
		fullName: '',
		email: '',
		password: '',
		confirmPassword: '',
		agreeTerms: false,
	});
	const [formError, setFormError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	
	const handleChange = (e) => {
		const {name, value, type, checked} = e.target;
		setFormData({
			...formData,
			[name]: type === 'checkbox' ? checked : value,
		});
	};
	
	const validateForm = () => {
		if (formData.password !== formData.confirmPassword) {
			setFormError(t('auth.passwordsDoNotMatch'));
			return false;
		}
		
		if (!formData.agreeTerms) {
			setFormError(t('auth.mustAgreeToTerms'));
			return false;
		}
		
		if (formData.password.length < 8) {
			setFormError(t('auth.passwordLengthError'));
			return false;
		}
		
		if (!/[A-Z]/.test(formData.password)) {
			setFormError(t('auth.passwordUppercaseError'));
			return false;
		}
		
		if (!/[a-z]/.test(formData.password)) {
			setFormError(t('auth.passwordLowercaseError'));
			return false;
		}
		
		if (!/\d/.test(formData.password)) {
			setFormError(t('auth.passwordNumberError'));
			return false;
		}
		
		return true;
	};
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		setFormError('');
		setSuccessMessage('');
		
		if (!validateForm()) {
			return;
		}
		
		try {
			const response = await register(formData.email, formData.password, formData.fullName);
			setSuccessMessage(t('auth.registrationSuccess'));
			
			// If user is registered and logged in, redirect to home
			if (response.user && response.token) {
				navigate('/');
			} else {
				// Otherwise navigate to login after a delay
				setTimeout(() => {
					navigate('/login');
				}, 3000);
			}
		} catch (err) {
			// Handle specific error cases
			if (err.message && err.message.includes('Email already in use')) {
				setFormError(t('auth.emailAlreadyInUse'));
			} else if (err.message && err.message.includes('Password')) {
				setFormError(err.message);
			} else {
				setFormError(err.message || t('auth.registrationError'));
			}
		}
	};
	
	const handleGoogleLoginSuccess = async (credentialResponse) => {
		try {
			await socialLogin('google', {
				credential: credentialResponse.credential,
				clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID
			});
			navigate('/');
		} catch (err) {
			setFormError(err.message || t('auth.loginError'));
		}
	};
	
	const handleGoogleLoginError = () => {
		setFormError(t('auth.googleLoginError'));
	};
	
	// Animation variants
	const containerVariants = {
		hidden: {opacity: 0},
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				delayChildren: 0.2,
			}
		}
	};
	
	const itemVariants = {
		hidden: {opacity: 0, y: 20},
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				type: "spring",
				stiffness: 260,
				damping: 20
			}
		}
	};
	
	return (
		<div className="flex justify-center items-center min-h-[80vh] p-4">
			{loading && <LoadingOverlay/>}
			
			<motion.div
				initial="hidden"
				animate="visible"
				variants={containerVariants}
				className="w-full max-w-md"
			>
				<Card variant="glass" hover="glow" className="w-full">
					<CardHeader>
						<motion.div
							className="w-16 h-16 rounded-2xl bg-secondary bg-opacity-10 dark:bg-secondary-dark dark:bg-opacity-20 flex items-center justify-center mb-4 mx-auto"
							variants={itemVariants}
						>
							<FaUserPlus className="text-secondary text-2xl"/>
						</motion.div>
						<CardTitle gradient={true} className="text-center">{t('auth.registerTitle')}</CardTitle>
						<CardDescription className="text-center">{t('auth.registerSubtitle')}</CardDescription>
					</CardHeader>
					
					<CardContent>
						<motion.form onSubmit={handleSubmit} variants={containerVariants}>
							{formError && (
								<motion.div
									variants={itemVariants}
									className="bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded relative mb-4"
								>
									{formError}
								</motion.div>
							)}
							
							{successMessage && (
								<motion.div
									variants={itemVariants}
									className="bg-green-100 dark:bg-green-900/30 border border-green-400 text-green-700 dark:text-green-300 px-4 py-3 rounded relative mb-4"
								>
									{successMessage}
								</motion.div>
							)}
							
							<motion.div className="mb-4" variants={itemVariants}>
								<label htmlFor="fullName" className="block text-sm font-medium mb-1">
									{t('auth.fullName')}
								</label>
								<input
									type="text"
									id="fullName"
									name="fullName"
									value={formData.fullName}
									onChange={handleChange}
									className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
									placeholder={t('auth.fullNamePlaceholder')}
									required
								/>
							</motion.div>
							
							<motion.div className="mb-4" variants={itemVariants}>
								<label htmlFor="email" className="block text-sm font-medium mb-1">
									{t('auth.email')}
								</label>
								<input
									type="email"
									id="email"
									name="email"
									value={formData.email}
									onChange={handleChange}
									className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
									placeholder={t('auth.emailPlaceholder')}
									required
								/>
							</motion.div>
							
							<motion.div className="mb-4" variants={itemVariants}>
								<label htmlFor="password" className="block text-sm font-medium mb-1">
									{t('auth.password')}
								</label>
								<input
									type="password"
									id="password"
									name="password"
									value={formData.password}
									onChange={handleChange}
									className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
									placeholder={t('auth.passwordPlaceholder')}
									required
									minLength="8"
								/>
								<div className="text-xs mt-1 text-text-light dark:text-text-light">
									<p>{t('auth.passwordRequirements')}</p>
									<ul className="list-disc pl-5 mt-1">
										<li className={formData.password.length >= 8 ? "text-green-600 dark:text-green-400" : ""}>
											{t('auth.passwordLength')}
										</li>
										<li className={/[A-Z]/.test(formData.password) ? "text-green-600 dark:text-green-400" : ""}>
											{t('auth.passwordUppercase')}
										</li>
										<li className={/[a-z]/.test(formData.password) ? "text-green-600 dark:text-green-400" : ""}>
											{t('auth.passwordLowercase')}
										</li>
										<li className={/\d/.test(formData.password) ? "text-green-600 dark:text-green-400" : ""}>
											{t('auth.passwordNumber')}
										</li>
									</ul>
								</div>
							</motion.div>
							
							<motion.div className="mb-6" variants={itemVariants}>
								<label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
									{t('auth.confirmPassword')}
								</label>
								<input
									type="password"
									id="confirmPassword"
									name="confirmPassword"
									value={formData.confirmPassword}
									onChange={handleChange}
									className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
									placeholder={t('auth.confirmPasswordPlaceholder')}
									required
								/>
							</motion.div>
							
							<motion.div className="flex items-center mb-6" variants={itemVariants}>
								<input
									type="checkbox"
									id="agreeTerms"
									name="agreeTerms"
									checked={formData.agreeTerms}
									onChange={handleChange}
									className="h-4 w-4 text-secondary focus:ring-secondary border-gray-300 rounded"
									required
								/>
								<label htmlFor="agreeTerms" className="ml-2 block text-sm">
									{t('auth.agreeTermsPrefix')} <a href="/terms"
																	className="text-secondary hover:underline">{t('auth.termsOfService')}</a> and <a
									href="/privacy"
									className="text-secondary hover:underline">{t('auth.privacyPolicy')}</a>
								</label>
							</motion.div>
							
							<motion.div variants={itemVariants}>
								<Button
									type="submit"
									variant="secondary"
									className="w-full"
									animation="shimmer"
									disabled={loading}
								>
									{loading ? t('common.loading') : t('auth.signUp')}
								</Button>
							</motion.div>
						</motion.form>
						
						<motion.div
							className="mt-6 relative flex items-center justify-center"
							variants={itemVariants}
						>
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
							</div>
							<div className="relative px-4 bg-background dark:bg-background-dark">
								{t('auth.orContinueWith')}
							</div>
						</motion.div>
						
						<motion.div className="mt-6" variants={itemVariants}>
							<div className="flex justify-center">
								<GoogleLogin
									onSuccess={handleGoogleLoginSuccess}
									onError={handleGoogleLoginError}
									text="continue_with"
									shape="pill"
									theme="filled_blue"
									size="large"
									width="100%"
								/>
							</div>
						</motion.div>
					</CardContent>
					
					<CardFooter className="flex justify-center">
						<motion.p variants={itemVariants} className="text-sm text-center">
							{t('auth.alreadyHaveAccount')} <Link to="/login"
																 className="text-secondary hover:underline">{t('auth.login')}</Link>
						</motion.p>
					</CardFooter>
				</Card>
			</motion.div>
		</div>
	);
};

export default Register;
